// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps, ReleaseComponentProps } from '@/typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import {
    AutomationRun,
    Integration,
    IntegrationCapability,
    IntegrationType,
    LifecycleEventType,
    ReleaseCalendarEventType,
    ReleaseCalendarViewType,
    ReleaseNav,
    ReleaseStatus,
} from '@/types';
import useSetLastViewedReleaseStep from './hooks/useSetLastViewedReleaseStep';
import Layout from 'antd/lib/layout';
import { antGrey, runwayColors, titleStyle, tagStyle } from './styles/globalStyles';
import Button from 'antd/lib/button';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    LeftOutlined,
    PauseOutlined,
    RedoOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAuthCredentials,
    selectIntegrationForCapability,
    selectRelease,
    selectIsDemoInstance,
    selectIsWorkflowsSSRStepsEnabled,
    selectDemoApp,
    selectAppIntegrations,
} from '@/selectors/selectors';
import CreateEditReleaseModal from './CreateEditReleaseModal';
import { PrepareNewWorkflowRunModal } from './prepareRelease/PrepareNewWorkflowRunModal';
import Space from 'antd/lib/space';
import Flex from 'antd/lib/flex';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { tryUpdateRelease } from '@/actions/release';
import { ReleaseUpdatable } from '@/actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '@/utils/globalMessage';
import Tooltip from 'antd/lib/tooltip';
import * as API from '../api';
import { GetReleaseCalendarRequest } from '@/actions/appSettings';
import {
    CalendarEventBaseData,
    capitalizeFirstLetter,
    getExternalEventsForReleaseCalendar,
    getLifecycleAndReleaseCycleEvents,
    ReleaseCalendarLifecycleFreezeEventData,
    ReleaseCalendarExternalEventData,
    ReleaseCalendarLifecycleEventData,
    ReleaseCalendarReleaseCycleEventData,
    sortCalendarEvents,
} from '@/helpers';
import { dayjs } from '../extendedDayjs';
import ReleaseCyclePopover from './ReleaseCyclePopover';
import ReleaseCalendarEventPopover from './ReleaseCalendarEventPopover';
import { Divider } from 'antd/lib';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import { trackReleaseRouteEvent } from '@/actions/trackEvent';
import * as AnalyticEvents from '../utils/analytics';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { getReleaseCalendarDemoData } from '../helpers/demoHelpers';
import Popover from 'antd/lib/popover';
import Checkbox from 'antd/lib/checkbox';
import Form from 'antd/lib/form';
import { FilterOutlined } from '@ant-design/icons';
import { ReleaseCalendarLifecycleEvent } from './ReleaseCalendarLifecycleEvent';
import { ReleaseCalendarExternalEvent } from './ReleaseCalendarExternalEvent';
import { ReleaseStepIntegrationStatusBanner } from './releaseSteps/ReleaseStepIntegrationStatusBanner';

const EmptyCalendarOverlay = () => (
    <div
        style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: antGrey.grey2,
            padding: '20px',
            borderRadius: '5px',
            textAlign: 'center',
            zIndex: 99999,
        }}
    >
        <Text style={{ margin: 100 }}>No release schedule dates found.</Text>
    </div>
);

const { Option } = Select;

interface ReleaseCalendarProps extends AppComponentProps {
    orgId: string;
    appId: string;
    lifecycleAutomations?: AutomationRun[];
    releaseId?: string;
    isCadenceLoading?: boolean;
}

export const ReleaseCalendar: React.FC<ReleaseCalendarProps> = (props) => {
    const dispatch = useDispatch();
    const calendarRef = React.useRef(null);
    const pageTitle = 'Release Schedule';
    const [view, setView] = React.useState(ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth);
    const [calendarLifecycleEvents, setCalendarLifecycleEvents] = React.useState<ReleaseCalendarLifecycleEventData[]>(
        [],
    );
    const [calendarReleaseCycleEvents, setCalendarReleaseCycleEvents] = React.useState<
        ReleaseCalendarReleaseCycleEventData[]
    >([]);
    const [calendarExternalEvents, setCalendarExternalEvents] = React.useState<ReleaseCalendarExternalEventData[]>([]);
    const [releaseLevelParentEvent, setReleaseLevelParentEvent] =
        React.useState<ReleaseCalendarReleaseCycleEventData | null>(null);
    const [hoveredReleaseVersion, setHoveredReleaseVersion] = React.useState<string | null>(null);
    const [filtersVisible, setFiltersVisible] = React.useState(false);
    const [hideSkippedReleases, setHideSkippedReleases] = React.useState(true);
    const [hideEvents, setHideEvents] = React.useState(false);
    const [form] = Form.useForm();
    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));
    const calendarIntegrationConnected = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, props.appId, IntegrationCapability.Calendar),
    );
    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, props.orgId));
    const demoApp = useSelector((state: RootState) => selectDemoApp(state, props.appId));
    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId));
    const isSSRMigrated = useSelector((state: RootState) => selectIsWorkflowsSSRStepsEnabled(state, props.appId));
    const calendarIntegrationsInErrorState = useSelector((state: RootState) => {
        const appIntegrations = selectAppIntegrations(state, props.appId);
        return appIntegrations.filter(
            (integration) =>
                (integration.id === IntegrationType.GoogleCalendar ||
                    integration.id === IntegrationType.GooglePersonalCalendar) &&
                integration.integrationError,
        );
    });
    const [hasFetchedReleaseCalendar, setHasFetchedReleaseCalendar] = React.useState(false);
    const [initialDate, setInitialDate] = React.useState<Date | null>(null);
    const [showEmptyState, setShowEmptyState] = React.useState(false);
    const [fetchCalendarIsLoading, setFetchCalendarIsLoading] = React.useState(false);
    const [calendarLifecycleFreezeEvents, setCalendarLifecycleFreezeEvents] = React.useState<
        ReleaseCalendarLifecycleFreezeEventData[]
    >([]);

    const { sortedCalendarLifecycleEvents, sortedCalendarReleaseCycleEvents, sortedLifecycleFreezeEvents } =
        React.useMemo(() => {
            if (!hoveredReleaseVersion && !hideSkippedReleases) {
                return {
                    sortedCalendarLifecycleEvents: hideEvents ? [] : calendarLifecycleEvents,
                    sortedCalendarReleaseCycleEvents: calendarReleaseCycleEvents,
                    sortedLifecycleFreezeEvents: calendarLifecycleFreezeEvents,
                };
            }

            const { sortedLifecycleEvents, sortedReleaseCycleEvents, sortedLifecycleFreezeEvents } = sortCalendarEvents(
                calendarLifecycleEvents,
                calendarReleaseCycleEvents,
                calendarLifecycleFreezeEvents,
                hoveredReleaseVersion,
                hideSkippedReleases,
            );

            return {
                sortedCalendarLifecycleEvents: hideEvents ? [] : sortedLifecycleEvents,
                sortedCalendarReleaseCycleEvents: sortedReleaseCycleEvents,
                sortedLifecycleFreezeEvents: sortedLifecycleFreezeEvents,
            };
        }, [
            calendarLifecycleEvents,
            calendarReleaseCycleEvents,
            calendarLifecycleFreezeEvents,
            hoveredReleaseVersion,
            hideSkippedReleases,
            hideEvents,
        ]);

    const handleChangeView = (value) => {
        if (calendarRef.current) {
            setView(value);
            calendarRef.current.getApi().changeView(value);
        }
    };

    const handlePrevClick = () => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.prev();
        }
    };

    const handleNextClick = () => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.next();
        }
    };

    const fetchReleaseCalendar = React.useCallback(() => {
        setFetchCalendarIsLoading(true);
        const requestData: GetReleaseCalendarRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
        };
        API.getReleaseCalendar(requestData, authCreds)
            .then((response) => {
                const releaseCalendarData = response.releaseCalendar;
                setHasFetchedReleaseCalendar(true);

                const [releaseCycleEvents, lifecycleEvents] = getLifecycleAndReleaseCycleEvents(releaseCalendarData);

                setCalendarLifecycleEvents(lifecycleEvents);
                setCalendarReleaseCycleEvents(releaseCycleEvents);
                setShowEmptyState(releaseCycleEvents.length === 0);

                let releaseCycleEvent = releaseCycleEvents.length === 1 ? releaseCycleEvents[0] : null;

                if (releaseCycleEvent) {
                    setReleaseLevelParentEvent(releaseCycleEvent);
                    setInitialDate(dayjs(releaseCycleEvent.start).toDate());
                } else {
                    setInitialDate(new Date());
                }

                if (response.externalEvents && !!calendarIntegrationConnected) {
                    const externalEvents = getExternalEventsForReleaseCalendar(
                        response.externalEvents,
                        calendarIntegrationConnected.id,
                        props.releaseId && releaseCycleEvent ? releaseCycleEvent : null,
                    );
                    setCalendarExternalEvents(externalEvents);
                }

                if (response.lifecycleFreezes?.length) {
                    const events: ReleaseCalendarLifecycleFreezeEventData[] = response.lifecycleFreezes.map(
                        ({ startDate, endDate, timezone }, index) => ({
                            id: `lifecycle-freeze-${index}`,
                            title: 'Lifecycle freeze',
                            allDay: true,
                            groupId: props.appId,
                            backgroundColor: antGrey.grey9,
                            start: startDate,
                            end: dayjs(endDate).add(1, 'days').startOf('day').toISOString(), // Need to add 1 day since the calendar always ends the range at midnight
                            timezone: timezone,
                            eventDateType: ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent,
                        }),
                    );
                    setCalendarLifecycleFreezeEvents(events);
                }
            })
            .catch((error) => {
                globalMessage.error(error.message);
                setInitialDate(new Date());
                setShowEmptyState(true);
                return;
            })
            .finally(() => {
                setFetchCalendarIsLoading(false);
            });
    }, [authCreds]);

    const setReleaseCalendarDemoData = (
        lifecycleEvents: ReleaseCalendarLifecycleEventData[],
        releaseCycleEvents: ReleaseCalendarReleaseCycleEventData[],
    ) => {
        setCalendarLifecycleEvents(lifecycleEvents);
        setCalendarReleaseCycleEvents(releaseCycleEvents);
        setShowEmptyState(releaseCycleEvents.length === 0);
        if (releaseCycleEvents.length === 1) {
            const releaseCycleEvent = releaseCycleEvents[0];
            setReleaseLevelParentEvent(releaseCycleEvent);
            setInitialDate(dayjs(releaseCycleEvent.start).toDate());
        } else {
            setInitialDate(new Date());
        }
        setHasFetchedReleaseCalendar(true);
    };

    React.useEffect(() => {
        if (!hasFetchedReleaseCalendar) {
            if (isDemoInstance) {
                setFetchCalendarIsLoading(true);
                const [releaseCycleEvents, lifecycleEvents] = getReleaseCalendarDemoData(demoApp, release);
                setReleaseCalendarDemoData(
                    lifecycleEvents as ReleaseCalendarLifecycleEventData[],
                    releaseCycleEvents as ReleaseCalendarReleaseCycleEventData[],
                );
            } else {
                fetchReleaseCalendar();
            }
        }
    }, []);

    React.useEffect(() => {
        return () => {
            setHoveredReleaseVersion(null);
        };
    }, []);

    const handleTodayClick = () => {
        calendarRef.current?.getApi().today();
    };

    const [editRunwayReleaseSettingsModalVisible, setEditRunwayReleaseSettingsModalVisible] = React.useState(false);
    const cadenceButtonText =
        releaseLevelParentEvent && releaseLevelParentEvent.scheduledAutomationsPaused ?
            'Resume schedule cadence'
        :   'Pause schedule cadence';
    const buttonIcon =
        releaseLevelParentEvent && releaseLevelParentEvent.scheduledAutomationsPaused ?
            <RedoOutlined style={{ color: runwayColors.info }} />
        :   <PauseOutlined style={{ color: runwayColors.statusYellow }} />;
    const tooltipText =
        releaseLevelParentEvent && releaseLevelParentEvent.scheduledAutomationsPaused ?
            'Runway will resume performing scheduled automations related to kickoff, submission, and release on their target dates for the current and first upcoming release.'
        :   'Runway will pause scheduled automations related to kickoff, submission, and release for all current and upcoming releases.';

    const handleOnEditTargetDates = () => {
        fetchReleaseCalendar();
    };

    const updateScheduleCadence = () => {
        if (releaseLevelParentEvent && releaseLevelParentEvent.scheduledAutomationsPaused) {
            dispatch(
                tryUpdateRelease(
                    ReleaseUpdatable.ResumeScheduleCadence,
                    {
                        orgId: props.orgId,
                        appId: props.appId,
                        releaseId: props.releaseId,
                    },
                    props.releaseId,
                ),
            );
        } else {
            dispatch(
                tryUpdateRelease(
                    ReleaseUpdatable.PauseScheduleCadence,
                    {
                        orgId: props.orgId,
                        appId: props.appId,
                        releaseId: props.releaseId,
                    },
                    props.releaseId,
                ),
            );
        }
    };

    const handleReleaseCycleEventHover = (displayVersion: string) => {
        setHoveredReleaseVersion(displayVersion);
    };

    const handleReleaseCycleEventLeave = () => {
        setHoveredReleaseVersion(null);
    };

    const renderReleaseCycleEvent = (event: ReleaseCalendarReleaseCycleEventData) => {
        const lifecycleEvents = calendarLifecycleEvents.filter(
            (releaseCalendarEvent) => event.groupId === releaseCalendarEvent.groupId,
        );
        const isSkippedRelease = event.status === ReleaseStatus.Skipped;
        const title = isSkippedRelease ? `${event.title} (skipped)` : event.title;
        const eventContent =
            view !== ReleaseCalendarViewType.ReleaseCalendarViewTypeList ?
                <div
                    style={{
                        backgroundColor: event.backgroundColor,
                        border: `1px solid ${event.parentEventColor}`,
                        opacity: 1,
                        position: 'relative',
                        padding: '10px',
                        height: '20px',
                        boxSizing: 'border-box',
                    }}
                    onMouseEnter={() => handleReleaseCycleEventHover(event.displayVersion)}
                    onMouseLeave={handleReleaseCycleEventLeave}
                >
                    <span
                        style={{
                            backgroundColor: event.parentEventColor,
                            padding: '2px 4px',
                            position: 'absolute',
                            top: '0px',
                            left: '0px',
                            zIndex: 1,
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {title}
                    </span>
                </div>
            :   <Text
                    onMouseEnter={() => handleReleaseCycleEventHover(event.displayVersion)}
                    onMouseLeave={handleReleaseCycleEventLeave}
                >
                    {title}
                </Text>;

        return (
            <ReleaseCyclePopover
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId ?? props.appId + event.id}
                parentBackgroundColor={event.parentEventColor}
                backgroundColor={event.backgroundColor}
                eventContent={eventContent}
                isHotfix={event.isHotfix}
                displayVersion={event.title}
                calendarEvents={lifecycleEvents}
                releasePilotId={event.releasePilotId}
                releaseDescription={event.releaseDescription}
                lifecycleAutomations={props.lifecycleAutomations ?? null}
                start={event.start}
                end={event.end}
                isSkippedRelease={isSkippedRelease}
            />
        );
    };

    const renderLifecycleFreezeEvent = (event: ReleaseCalendarLifecycleFreezeEventData) => {
        const title = capitalizeFirstLetter(event.title);
        const end = event.end && dayjs(event.end).subtract(1, 'days').toISOString(); // Fixing date since we had to add 1 day earlier in the code
        const eventContent =
            view !== ReleaseCalendarViewType.ReleaseCalendarViewTypeList ?
                <div
                    style={{
                        backgroundColor: event.backgroundColor,
                        border: `1px solid ${event.backgroundColor}`,
                        opacity: 1,
                        position: 'relative',
                        padding: '10px',
                        height: '25px',
                        maxHeight: '25px',
                        boxSizing: 'border-box',
                    }}
                >
                    <span
                        style={{
                            backgroundColor: event.backgroundColor,
                            padding: '2px 4px',
                            position: 'absolute',
                            top: '0px',
                            left: '0px',
                            zIndex: 1,
                            height: '20px',
                            maxHeight: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {title}
                    </span>
                </div>
            :   <Text>{title}</Text>;
        return (
            <ReleaseCalendarEventPopover
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId ?? props.appId + event.id}
                backgroundColor={event.backgroundColor}
                eventContent={eventContent}
                lifecycleAutomations={null}
                eventDateType={ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent}
                eventType={LifecycleEventType.LifecycleEventTypeLifecycleFreeze}
                isEventComplete={false}
                eventDate={event.start}
                isTargetDateScheduledFromCadence={false}
                incrementDayNumber={0}
                stagedRolloutPercentage={0}
                isSkippedRelease={false}
                title={event.title}
                isExternalEvent={false}
                eventEndDate={end}
                timezone={event.timezone}
                allDay={event.allDay}
            />
        );
    };

    const renderExternalEvent = (event: ReleaseCalendarExternalEventData) => {
        return (
            <ReleaseCalendarExternalEvent
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId ?? props.appId + event.id}
                event={event}
                view={view}
                integration={calendarIntegrationConnected}
            />
        );
    };
    const renderReleaseLifecycleEvent = (event: ReleaseCalendarLifecycleEventData) => {
        return (
            <ReleaseCalendarLifecycleEvent
                event={event}
                lifecycleAutomations={props.lifecycleAutomations}
                appId={props.appId}
                orgId={props.orgId}
                releaseId={props.releaseId}
                view={view}
            />
        );
    };

    const renderCalendarSyncIntegration = (integration: Integration | undefined) => {
        if (!integration || integration.integrationError) {
            return null;
        }
        const [icon, alt] = iconAndAltForIntegrationType(integration.id);
        const calendarTooltipDesc = displayNameForIntegrationType(integration.id) + ' is connected and synced';
        return (
            <Tooltip
                title={calendarTooltipDesc}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                placement="bottom"
            >
                <Space style={{ gap: '12px' }}>
                    <img src={icon} alt={alt} style={{ width: '16px', height: '16px', marginBottom: '3px' }} />
                    <CheckCircleOutlined
                        style={{
                            color: runwayColors.statusGreen,
                            width: '16px',
                            height: '16px',
                        }}
                    />
                </Space>
                <Divider
                    type="vertical"
                    style={{
                        height: '20px',
                        width: '5px',
                        borderColor: antGrey.grey5,
                        marginLeft: 12,
                        marginRight: 12,
                    }}
                />
            </Tooltip>
        );
    };

    const renderEventContent = (eventInfo: any) => {
        const event = eventInfo.event;
        const eventExtendedProps = eventInfo.event.extendedProps;

        const isParentEvent =
            eventExtendedProps.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeParent;
        const isExternalEvent =
            eventExtendedProps.eventDateType == ReleaseCalendarEventType.ReleaseCalendarExternalEvent;
        const isFreezeEvent =
            eventExtendedProps.eventDateType == ReleaseCalendarEventType.ReleaseCalendarLifecycleFreezeEvent;

        const baseCalendarEvent: CalendarEventBaseData = {
            id: event.id,
            groupId: event.groupId,
            title: event.title,
            allDay: event.allDay,
            start: event.start,
            end: event.end,
            backgroundColor: event.backgroundColor,
        };

        if (isParentEvent) {
            return renderReleaseCycleEvent({
                ...baseCalendarEvent,
                ...eventExtendedProps,
            });
        }

        if (isExternalEvent) {
            return renderExternalEvent({
                ...baseCalendarEvent,
                ...eventExtendedProps,
            });
        }

        if (isFreezeEvent) {
            return renderLifecycleFreezeEvent({
                ...baseCalendarEvent,
                ...eventExtendedProps,
            });
        }

        return renderReleaseLifecycleEvent({
            ...baseCalendarEvent,
            ...eventExtendedProps,
        });
    };

    const customLocale = {
        code: 'custom',
        allDayText: view === ReleaseCalendarViewType.ReleaseCalendarViewTypeList ? 'Release' : '',
    };

    const handleDayCellClassNames = (arg: { date: Date }) => {
        if (dayjs(arg.date).isSame(dayjs(), 'day')) {
            return ['release-calendar-today-highlight'];
        }
        return [];
    };

    const applyFilters = (values: { hideEvents: boolean; hideSkippedReleases: boolean }) => {
        setHideEvents(values.hideEvents);
        setHideSkippedReleases(values.hideSkippedReleases);
        setFiltersVisible(false);
    };

    const clearButtonClicked = () => {
        setHideEvents(false);
        setHideSkippedReleases(false);
        form.setFieldsValue({ hideEvents: false, hideSkippedReleases: false });
    };

    const renderFilterModalContent = () => (
        <div style={{ minWidth: 200 }}>
            <div style={{ padding: 12 }}>
                <Form
                    form={form}
                    initialValues={{ hideEvents, hideSkippedReleases }}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                >
                    <Flex vertical gap={12}>
                        <Form.Item name="hideEvents" valuePropName="checked" noStyle>
                            <Checkbox>Hide events</Checkbox>
                        </Form.Item>
                        <Form.Item name="hideSkippedReleases" valuePropName="checked" noStyle>
                            <Checkbox>Hide skipped releases</Checkbox>
                        </Form.Item>
                    </Flex>
                </Form>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={clearButtonClicked}>
                    Clear
                </Button>
                <Button size="small" onClick={form.submit} type="primary">
                    Apply
                </Button>
            </div>
        </div>
    );

    return (
        <Layout
            style={{
                backgroundColor: runwayColors.white,
                position: 'relative',
            }}
        >
            <ReleaseStepIntegrationStatusBanner
                orgId={props.orgId}
                appId={props.appId}
                integrations={calendarIntegrationsInErrorState}
            />
            <div
                style={{
                    padding: '24px 36px 95px 36px',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                    }}
                >
                    <div>
                        <Text style={{ ...titleStyle, marginRight: 8 }} type="secondary">
                            {pageTitle}
                        </Text>
                        <Button type="link" size="small" style={{ color: antGrey.grey7 }} onClick={handlePrevClick}>
                            {<LeftOutlined />}
                        </Button>
                        <Button type="link" size="small" style={{ color: antGrey.grey7 }} onClick={handleNextClick}>
                            {<RightOutlined />}
                        </Button>
                        <Button
                            type="default"
                            size="small"
                            style={{ color: antGrey.grey7, marginLeft: 10 }}
                            onClick={handleTodayClick}
                        >
                            Today
                        </Button>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <div>
                            {props.releaseId && releaseLevelParentEvent && (
                                <PermissionedActionButton
                                    icon={<CalendarOutlined style={{ color: runwayColors.info }} />}
                                    onClick={() => setEditRunwayReleaseSettingsModalVisible(true)}
                                    disabled={releaseLevelParentEvent && releaseLevelParentEvent.isReleaseCompleted}
                                    type="link"
                                >
                                    Edit target dates
                                </PermissionedActionButton>
                            )}

                            {props.releaseId && (
                                <>
                                    <Space>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAPauseResumeScheduleCadence}
                                            releaseId={props.releaseId}
                                            icon={buttonIcon}
                                            onClick={updateScheduleCadence}
                                            loading={props.isCadenceLoading}
                                        >
                                            {cadenceButtonText}
                                        </PermissionedActionButton>
                                        <Tooltip title={tooltipText} placement="topRight">
                                            <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                        </Tooltip>
                                    </Space>

                                    <Divider
                                        type="vertical"
                                        style={{
                                            height: '20px',
                                            width: '5px',
                                            borderColor: antGrey.grey5,
                                            marginLeft: 12,
                                            marginRight: 12,
                                        }}
                                    />
                                </>
                            )}
                            {!props.releaseId && (
                                <>
                                    <Popover
                                        overlayClassName="filters-overlay"
                                        destroyTooltipOnHide
                                        content={renderFilterModalContent()}
                                        title={
                                            <Text
                                                style={{ ...tagStyle, fontSize: 12, color: antGrey.grey7, padding: 15 }}
                                            >
                                                Filters
                                            </Text>
                                        }
                                        trigger="click"
                                        open={filtersVisible}
                                        placement="left"
                                        onOpenChange={(open) => setFiltersVisible(open)}
                                    >
                                        <Button
                                            icon={<FilterOutlined style={{ color: runwayColors.primary }} />}
                                            type="default"
                                        >
                                            Filter
                                        </Button>
                                    </Popover>
                                    <Divider
                                        type="vertical"
                                        style={{
                                            height: '20px',
                                            width: '5px',
                                            borderColor: antGrey.grey5,
                                            marginLeft: 12,
                                            marginRight: 12,
                                        }}
                                    />
                                </>
                            )}
                            {renderCalendarSyncIntegration(calendarIntegrationConnected)}

                            <Select
                                defaultValue={ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth}
                                style={{ width: 90 }}
                                onChange={handleChangeView}
                            >
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeMonth}>Month</Option>
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeWeek}>Week</Option>
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeDay}>Day</Option>
                                <Option value={ReleaseCalendarViewType.ReleaseCalendarViewTypeList}>List</Option>
                            </Select>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    <div>
                        {fetchCalendarIsLoading && !hasFetchedReleaseCalendar && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100vh',
                                    flexGrow: 1,
                                }}
                            >
                                <Spin />
                            </div>
                        )}
                        {initialDate && (
                            <div
                                style={
                                    {
                                        opacity: showEmptyState ? '50%' : null,
                                    } as React.CSSProperties
                                }
                            >
                                <FullCalendar
                                    ref={calendarRef}
                                    initialDate={showEmptyState ? null : initialDate}
                                    timeZone="local"
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                                    initialView={view}
                                    events={
                                        showEmptyState ? null
                                        : calendarExternalEvents ?
                                            [
                                                ...sortedLifecycleFreezeEvents.map((event) => ({
                                                    ...event,
                                                    customOrder: -2,
                                                })),
                                                ...sortedCalendarReleaseCycleEvents.map((event) => ({
                                                    ...event,
                                                    customOrder: -1,
                                                })),
                                                ...sortedCalendarLifecycleEvents,
                                                ...calendarExternalEvents.map((event) => ({
                                                    ...event,
                                                    customOrder: 999,
                                                })),
                                            ]
                                        :   [
                                                ...sortedLifecycleFreezeEvents.map((event) => ({
                                                    ...event,
                                                    customOrder: -2,
                                                })),
                                                ...sortedCalendarReleaseCycleEvents.map((event) => ({
                                                    ...event,
                                                    customOrder: -1,
                                                })),
                                                ...sortedCalendarLifecycleEvents,
                                            ]

                                    }
                                    eventContent={renderEventContent}
                                    headerToolbar={{
                                        left: '',
                                        center: 'title',
                                        right: '',
                                    }}
                                    dayHeaders={true}
                                    dayHeaderFormat={{ weekday: 'long' }}
                                    locales={[customLocale]}
                                    locale="custom"
                                    dayCellClassNames={handleDayCellClassNames}
                                    dayMaxEvents={3}
                                    eventOrder="customOrder,start,-duration,allDay,title"
                                    eventDidMount={(info) => {
                                        if (info.el.closest('.fc-timeGridDay-view')) {
                                            info.el.style.paddingTop = '10px';
                                        }
                                        if (info.el.closest('.fc-popover-body')) {
                                            info.el.style.marginLeft = '-1px';
                                            info.el.style.maxWidth = '250px';
                                        }
                                    }}
                                />
                            </div>
                        )}
                        {showEmptyState && <EmptyCalendarOverlay />}
                    </div>
                </div>
                {props.releaseId &&
                    releaseLevelParentEvent &&
                    (isSSRMigrated ?
                        <PrepareNewWorkflowRunModal
                            orgId={props.orgId}
                            appId={props.appId}
                            open={editRunwayReleaseSettingsModalVisible}
                            mode="standard"
                            isEditingRelease={true}
                            releaseId={props.releaseId}
                            displayOnlyTargetDates={true}
                            canBeSubmitted={releaseLevelParentEvent.canReleaseBeSubmitted}
                            onCancel={() => {
                                setEditRunwayReleaseSettingsModalVisible(false);
                            }}
                            onSuccess={() => {
                                setEditRunwayReleaseSettingsModalVisible(false);
                            }}
                            onEditTargetDates={handleOnEditTargetDates}
                        />
                    :   <CreateEditReleaseModal
                            orgId={props.orgId}
                            appId={props.appId}
                            open={editRunwayReleaseSettingsModalVisible}
                            onDidCreateOrEditRelease={() => {
                                setEditRunwayReleaseSettingsModalVisible(false);
                            }}
                            onCancel={() => {
                                setEditRunwayReleaseSettingsModalVisible(false);
                            }}
                            isEditingRelease={true}
                            releaseId={props.releaseId}
                            displayOnlyTargetDates={true}
                            canBeSubmitted={releaseLevelParentEvent.canReleaseBeSubmitted}
                            onEditTargetDates={handleOnEditTargetDates}
                        />)}
            </div>
        </Layout>
    );
};

const ReleaseCalendarContainer: React.FC<ReleaseComponentProps> = (props: ReleaseComponentProps) => {
    const dispatch = useDispatch();

    useSetLastViewedReleaseStep(props.releaseId, ReleaseNav.ReleaseCalendar);
    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                eventName: AnalyticEvents.Event.ViewReleaseCalendar,
            }),
        );
    }, []);

    const lifecycleAutomations = useSelector(
        (state: RootState) => selectRelease(state, props.releaseId).lifecycleAutomations,
    );

    const resumeCadenceIsLoading = useIsLoading(ReleaseUpdatable.ResumeScheduleCadence, props.releaseId);
    const resumeCadenceSuccess = useUpdated(ReleaseUpdatable.ResumeScheduleCadence, props.releaseId);
    const resumeCadenceError = useError(ReleaseUpdatable.ResumeScheduleCadence, props.releaseId);

    const pauseCadenceIsLoading = useIsLoading(ReleaseUpdatable.PauseScheduleCadence, props.releaseId);
    const pauseCadenceSuccess = useUpdated(ReleaseUpdatable.PauseScheduleCadence, props.releaseId);
    const pauseCadenceError = useError(ReleaseUpdatable.PauseScheduleCadence, props.releaseId);

    React.useEffect(() => {
        document.title = 'Release Schedule';
    }, []);

    React.useEffect(() => {
        if (resumeCadenceSuccess) {
            globalMessage.success('Release schedule now resumed!');
        }
    }, [resumeCadenceSuccess]);

    React.useEffect(() => {
        if (pauseCadenceSuccess) {
            globalMessage.success('Release schedule now paused!');
        }
    }, [pauseCadenceSuccess]);

    React.useEffect(() => {
        if (resumeCadenceError) {
            globalMessage.error(resumeCadenceError);
        }
    }, [resumeCadenceError]);

    React.useEffect(() => {
        if (pauseCadenceError) {
            globalMessage.error(pauseCadenceError);
        }
    }, [pauseCadenceError]);

    return (
        <ReleaseCalendar
            orgId={props.orgId}
            appId={props.appId}
            releaseId={props.releaseId}
            lifecycleAutomations={lifecycleAutomations}
            isCadenceLoading={pauseCadenceIsLoading || resumeCadenceIsLoading}
        />
    );
};

export default ReleaseCalendarContainer;
